import type {
    DataManagementAlias,
    DataManagementAliasListResponse,
    DataManagementDataset,
    DataManagementDatasetListResponse,
    DataManagementSource,
    DataManagementSourceDetail,
    DataManagementSourceListResponse,
    DatasetIngestionArtifactListResponse,
    DatasetIngestionDownloadResponse,
    DatasetIngestionMappingDecision,
    DatasetIngestionMappingDecisionListResponse,
    DatasetIngestionParseRequest,
    DatasetIngestionReprocessRequest,
    DatasetIngestionRetryResponse,
    DatasetIngestionRun,
    DatasetIngestionRunCreateRequest,
    DatasetIngestionRunItem,
    DatasetIngestionRunItemListResponse,
    DatasetIngestionRunListResponse,
    DatasetIngestionRunProgress,
    DatasetSourceFile,
    DatasetSourceFileAnalysis,
    DatasetSourceFileListResponse,
    DatasetSourceFileRegistrationRequest,
    DatasetSourcePrefixRegistrationRequest,
    DatasetSourceRegistrationItem,
    DatasetSourceRegistrationItemListResponse,
    DatasetSourceRegistrationProgress,
    SdsIngestionRunCreateRequest,
    SdsMaterializationReport,
} from "../../data/models/schemas";
import { createRsaaAction } from "../../components/utilities/ReduxUtils";

const type = (name: string) => `DATA_MANAGEMENT_${name}`;

export const listDataManagementDatasets = createRsaaAction<
    void,
    DataManagementDatasetListResponse
>({
    path: "data-management/datasets",
    method: "GET",
    type: type("LIST_DATASETS"),
});
export const getDataManagementDataset = createRsaaAction<
    void,
    DataManagementDataset
>({
    path: "data-management/datasets/:datasetId",
    method: "GET",
    type: type("GET_DATASET"),
});
export const createDataManagementDataset = createRsaaAction<
    DataManagementDataset,
    DataManagementDataset
>({
    path: "data-management/datasets",
    method: "POST",
    type: type("CREATE_DATASET"),
});
export const updateDataManagementDataset = createRsaaAction<
    DataManagementDataset,
    DataManagementDataset
>({
    path: "data-management/datasets/:datasetId",
    method: "PUT",
    type: type("UPDATE_DATASET"),
});
export const deleteDataManagementDataset = createRsaaAction<void, void>({
    path: "data-management/datasets/:datasetId",
    method: "DELETE",
    type: type("DELETE_DATASET"),
});

export const listDatasetAliases = createRsaaAction<
    void,
    DataManagementAliasListResponse
>({
    path: "data-management/datasets/:datasetId/aliases",
    method: "GET",
    type: type("LIST_DATASET_ALIASES"),
});
export const createDatasetAlias = createRsaaAction<
    DataManagementAlias,
    DataManagementAlias
>({
    path: "data-management/datasets/:datasetId/aliases",
    method: "POST",
    type: type("CREATE_DATASET_ALIAS"),
});
export const updateDatasetAlias = createRsaaAction<
    DataManagementAlias,
    DataManagementAlias
>({
    path: "data-management/datasets/:datasetId/aliases/:aliasId",
    method: "PUT",
    type: type("UPDATE_DATASET_ALIAS"),
});
export const deleteDatasetAlias = createRsaaAction<void, void>({
    path: "data-management/datasets/:datasetId/aliases/:aliasId",
    method: "DELETE",
    type: type("DELETE_DATASET_ALIAS"),
});

export const listDataManagementSources = createRsaaAction<
    void,
    DataManagementSourceListResponse
>({
    path: "data-management/sources",
    method: "GET",
    type: type("LIST_SOURCES"),
});
export const getDataManagementSource = createRsaaAction<
    void,
    DataManagementSourceDetail
>({
    path: "data-management/sources/:sourceId",
    method: "GET",
    type: type("GET_SOURCE"),
});
export const createDataManagementSource = createRsaaAction<
    DataManagementSource,
    DataManagementSource
>({
    path: "data-management/sources",
    method: "POST",
    type: type("CREATE_SOURCE"),
});
export const updateDataManagementSource = createRsaaAction<
    DataManagementSource,
    DataManagementSource
>({
    path: "data-management/sources/:sourceId",
    method: "PUT",
    type: type("UPDATE_SOURCE"),
});
export const listSourceAliases = createRsaaAction<
    void,
    DataManagementAliasListResponse
>({
    path: "data-management/sources/:sourceId/aliases",
    method: "GET",
    type: type("LIST_SOURCE_ALIASES"),
});
export const createSourceAlias = createRsaaAction<
    DataManagementAlias,
    DataManagementAlias
>({
    path: "data-management/sources/:sourceId/aliases",
    method: "POST",
    type: type("CREATE_SOURCE_ALIAS"),
});
export const updateSourceAlias = createRsaaAction<
    DataManagementAlias,
    DataManagementAlias
>({
    path: "data-management/sources/:sourceId/aliases/:aliasId",
    method: "PUT",
    type: type("UPDATE_SOURCE_ALIAS"),
});
export const deleteSourceAlias = createRsaaAction<void, void>({
    path: "data-management/sources/:sourceId/aliases/:aliasId",
    method: "DELETE",
    type: type("DELETE_SOURCE_ALIAS"),
});

export const listDatasetSourceFiles = createRsaaAction<
    void,
    DatasetSourceFileListResponse
>({
    path: "data-management/datasets/:datasetId/source-files",
    method: "GET",
    type: type("LIST_SOURCE_FILES"),
});
export const getDatasetSourceFile = createRsaaAction<void, DatasetSourceFile>({
    path: "data-management/datasets/:datasetId/source-files/:sourceFileId",
    method: "GET",
    type: type("GET_SOURCE_FILE"),
});
export const analyzeDatasetSourceFile = createRsaaAction<
    void,
    DatasetSourceFileAnalysis
>({
    path: "data-management/datasets/:datasetId/source-files/:sourceFileId/analysis",
    method: "GET",
    type: type("ANALYZE_SOURCE_FILE"),
});
export const registerDatasetSourceFile = createRsaaAction<
    DatasetSourceFileRegistrationRequest,
    DatasetSourceFile
>({
    path: "data-management/datasets/:datasetId/source-files/register",
    method: "POST",
    type: type("REGISTER_SOURCE_FILE"),
});
export const registerDatasetSourcePrefix = createRsaaAction<
    DatasetSourcePrefixRegistrationRequest,
    DatasetIngestionRun
>({
    path: "data-management/datasets/:datasetId/source-files/register-prefix",
    method: "POST",
    type: type("REGISTER_SOURCE_PREFIX"),
});

export const listDataManagementIngestionRuns = createRsaaAction<
    void,
    DatasetIngestionRunListResponse
>({
    path: "data-management/ingestion-runs",
    method: "GET",
    type: type("LIST_ALL_RUNS"),
});
export const listDatasetIngestionRuns = createRsaaAction<
    void,
    DatasetIngestionRunListResponse
>({
    path: "data-management/datasets/:datasetId/ingestion-runs",
    method: "GET",
    type: type("LIST_RUNS"),
});
export const getDatasetIngestionRun = createRsaaAction<
    void,
    DatasetIngestionRun
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId",
    method: "GET",
    type: type("GET_RUN"),
});
export const createDatasetIngestionRun = createRsaaAction<
    DatasetIngestionRunCreateRequest,
    DatasetIngestionRun
>({
    path: "data-management/datasets/:datasetId/ingestion-runs",
    method: "POST",
    type: type("CREATE_RUN"),
});
export const createSdsIngestionRun = createRsaaAction<
    SdsIngestionRunCreateRequest,
    DatasetIngestionRun
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/sds",
    method: "POST",
    type: type("CREATE_SDS_RUN"),
});
export const parseDatasetIngestionRun = createRsaaAction<
    DatasetIngestionParseRequest,
    DatasetIngestionRun
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/parse",
    method: "POST",
    type: type("PARSE_RUN"),
});
export const applyDatasetIngestionMappings = createRsaaAction<
    void,
    DatasetIngestionRun
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/apply-mappings",
    method: "POST",
    type: type("APPLY_MAPPINGS"),
});
export const importDatasetIngestionRun = createRsaaAction<
    void,
    DatasetIngestionRun
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/import",
    method: "POST",
    type: type("IMPORT_RUN"),
});
export const reprocessDatasetIngestionRun = createRsaaAction<
    DatasetIngestionReprocessRequest,
    DatasetIngestionRun
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/reprocess",
    method: "POST",
    type: type("REPROCESS_RUN"),
});

export const listDatasetIngestionMappings = createRsaaAction<
    void,
    DatasetIngestionMappingDecisionListResponse
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/mappings",
    method: "GET",
    type: type("LIST_MAPPINGS"),
});
export const updateDatasetIngestionMapping = createRsaaAction<
    DatasetIngestionMappingDecision,
    DatasetIngestionMappingDecision
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/mappings/:decisionId",
    method: "PUT",
    type: type("UPDATE_MAPPING"),
});

export const getDatasetIngestionRunProgress = createRsaaAction<
    void,
    DatasetIngestionRunProgress
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/progress",
    method: "GET",
    type: type("GET_RUN_PROGRESS"),
});
export const listDatasetIngestionRunItems = createRsaaAction<
    void,
    DatasetIngestionRunItemListResponse
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/items",
    method: "GET",
    type: type("LIST_RUN_ITEMS"),
});
export const retryDatasetIngestionRunItem = createRsaaAction<
    void,
    DatasetIngestionRunItem
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/items/:itemId/retry",
    method: "POST",
    type: type("RETRY_RUN_ITEM"),
});
export const retryFailedDatasetIngestionRunItems = createRsaaAction<
    void,
    DatasetIngestionRetryResponse
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/retry-failed",
    method: "POST",
    type: type("RETRY_FAILED_RUN_ITEMS"),
});

export const getDatasetSourceRegistrationProgress = createRsaaAction<
    void,
    DatasetSourceRegistrationProgress
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/registration-progress",
    method: "GET",
    type: type("GET_REGISTRATION_PROGRESS"),
});
export const listDatasetSourceRegistrationItems = createRsaaAction<
    void,
    DatasetSourceRegistrationItemListResponse
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/registration-items",
    method: "GET",
    type: type("LIST_REGISTRATION_ITEMS"),
});
export const retryDatasetSourceRegistrationItem = createRsaaAction<
    void,
    DatasetSourceRegistrationItem
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/registration-items/:itemId/retry",
    method: "POST",
    type: type("RETRY_REGISTRATION_ITEM"),
});
export const retryFailedDatasetSourceRegistrationItems = createRsaaAction<
    void,
    DatasetIngestionRetryResponse
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/registration-retry-failed",
    method: "POST",
    type: type("RETRY_FAILED_REGISTRATION_ITEMS"),
});

export const listDatasetIngestionArtifacts = createRsaaAction<
    void,
    DatasetIngestionArtifactListResponse
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/artifacts",
    method: "GET",
    type: type("LIST_ARTIFACTS"),
});
export const getDatasetIngestionArtifactDownload = createRsaaAction<
    void,
    DatasetIngestionDownloadResponse
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/artifacts/:artifactId/download",
    method: "GET",
    type: type("GET_ARTIFACT_DOWNLOAD"),
});
export const materializeSdsIngestionRun = createRsaaAction<
    void,
    SdsMaterializationReport
>({
    path: "data-management/datasets/:datasetId/ingestion-runs/:runId/materialize",
    method: "POST",
    type: type("MATERIALIZE_SDS_RUN"),
});
